/**
 * PdfExport Store
 *
 * Manages the state for PDF export settings including layout, paper size, and margins.
 * Persisted at the WORKSHEET level to maintain user's export preferences per worksheet.
 */

import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { parsePageMargin } from '@/reportEditor/pdfMargin.utilities';
import { DOCUMENT_LAYOUT, DOCUMENT_PAPER_SIZE, DefaultMargin } from '@/reportEditor/report.constants';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type ActionsFactory, createStore, createStoreInstance } from '@/stores/createStore';
import type { DocumentLayout, DocumentPaperSize, Margin, PageMargins } from '@/reportEditor/report.constants';

export interface PdfExportState {
  /** Whether the PDF preview modal is currently visible. */
  showModal: boolean;
  /** Page layout orientation (Portrait or Landscape). */
  layout: DocumentLayout;
  /** Paper size for the PDF document. */
  paperSize: DocumentPaperSize;
  /** Left margin configuration. */
  marginLeft: Margin;
  /** Right margin configuration. */
  marginRight: Margin;
  /** Top margin configuration. */
  marginTop: Margin;
  /** Bottom margin configuration. */
  marginBottom: Margin;
}

export interface PdfExportActions {
  setShowModal: (showModal: boolean) => void;
  setLayout: (layout: DocumentLayout) => void;
  setPaperSize: (paperSize: DocumentPaperSize) => void;
  setMarginLeft: (margin: Margin) => void;
  setMarginRight: (margin: Margin) => void;
  setMarginTop: (margin: Margin) => void;
  setMarginBottom: (margin: Margin) => void;
  setAllOptions: (params: {
    pageSize: string;
    pageOrientation: string;
    marginLeft: string;
    marginRight: string;
    marginTop: string;
    marginBottom: string;
  }) => void;
  reset: () => void;
}

const initialState: PdfExportState = {
  showModal: false,
  layout: DOCUMENT_LAYOUT[0], // Portrait
  paperSize: DOCUMENT_PAPER_SIZE[0], // Letter
  marginLeft: { ...DefaultMargin },
  marginRight: { ...DefaultMargin },
  marginTop: { ...DefaultMargin },
  marginBottom: { ...DefaultMargin },
};

/** Dehydrated state excludes transient modal visibility. */
export type PdfExportDehydratedState = Omit<PdfExportState, 'showModal'>;

export const dehydratePdfExportState = (state: PdfExportState): PdfExportDehydratedState => ({
  layout: state.layout,
  paperSize: state.paperSize,
  marginLeft: state.marginLeft,
  marginRight: state.marginRight,
  marginTop: state.marginTop,
  marginBottom: state.marginBottom,
});

const pdfExportActionsFactory: ActionsFactory<PdfExportState, PdfExportActions> = (set, _get, resetState) => ({
  /** Sets the visibility of the PDF preview modal. */
  setShowModal: (showModal: boolean) =>
    set((draft: PdfExportState) => {
      draft.showModal = showModal;
    }),

  /** Sets the page layout orientation. */
  setLayout: (layout: DocumentLayout) =>
    set((draft: PdfExportState) => {
      draft.layout = layout;
    }),

  /** Sets the paper size. */
  setPaperSize: (paperSize: DocumentPaperSize) =>
    set((draft: PdfExportState) => {
      draft.paperSize = paperSize;
    }),

  /** Sets the left margin. */
  setMarginLeft: (margin: Margin) =>
    set((draft: PdfExportState) => {
      draft.marginLeft = { value: margin.value, units: margin.units };
    }),

  /** Sets the right margin. */
  setMarginRight: (margin: Margin) =>
    set((draft: PdfExportState) => {
      draft.marginRight = { value: margin.value, units: margin.units };
    }),

  /** Sets the top margin. */
  setMarginTop: (margin: Margin) =>
    set((draft: PdfExportState) => {
      draft.marginTop = { value: margin.value, units: margin.units };
    }),

  /** Sets the bottom margin. */
  setMarginBottom: (margin: Margin) =>
    set((draft: PdfExportState) => {
      draft.marginBottom = { value: margin.value, units: margin.units };
    }),

  /**
   * Sets all PDF options from a report annotation output.
   * Used when loading report settings from saved properties.
   */
  setAllOptions: (params: {
    pageSize: string;
    pageOrientation: string;
    marginLeft: string;
    marginRight: string;
    marginTop: string;
    marginBottom: string;
  }) =>
    set((draft: PdfExportState) => {
      draft.marginTop = parsePageMargin(params.marginTop, draft.marginTop);
      draft.marginBottom = parsePageMargin(params.marginBottom, draft.marginBottom);
      draft.marginLeft = parsePageMargin(params.marginLeft, draft.marginLeft);
      draft.marginRight = parsePageMargin(params.marginRight, draft.marginRight);

      const foundPaperSize = DOCUMENT_PAPER_SIZE.find((size) => size.value === params.pageSize);
      if (foundPaperSize) {
        draft.paperSize = foundPaperSize;
      }

      const foundLayout = DOCUMENT_LAYOUT.find((layout) => layout.value === params.pageOrientation);
      if (foundLayout) {
        draft.layout = foundLayout;
      }
    }),

  reset: resetState,
});

const STORE_NAME = 'PdfExport';

const [PdfExportStoreProvider, usePdfExportStoreInstance, usePdfExportSelector] = createStore(
  STORE_NAME,
  initialState,
  pdfExportActionsFactory,
);

export { PdfExportStoreProvider };

// Selector hooks
export const useShowModal = () => usePdfExportSelector((state) => state.showModal);
export const useLayout = () => usePdfExportSelector((state) => state.layout);
export const usePaperSize = () => usePdfExportSelector((state) => state.paperSize);

/** Derived selector that combines all margins into a PageMargins object. */
const selectMargins = createSelector(
  (s: PdfExportState) => s.marginLeft,
  (s: PdfExportState) => s.marginRight,
  (s: PdfExportState) => s.marginTop,
  (s: PdfExportState) => s.marginBottom,
  (marginLeft, marginRight, marginTop, marginBottom): PageMargins => ({
    left: marginLeft,
    right: marginRight,
    top: marginTop,
    bottom: marginBottom,
  }),
);
export const useMargins = () => usePdfExportSelector(selectMargins);

// Actions hook
export const usePdfExportActions = () => usePdfExportStoreInstance().actions;

/**
 * Creates a standalone store instance for tests or imperative access.
 * For tests, prefer using initialPdfExportData on TestStoreProvider when only setting initial state.
 */
export function createTestPdfExportStore(overrides?: Partial<PdfExportState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestPdfExportStore() should only be used in tests.');
  }

  return createStoreInstance({ ...initialState, ...overrides }, pdfExportActionsFactory);
}

/**
 * @deprecated This function is ONLY for legacy migration from Flux/Baobab stores.
 * DO NOT use in new code.
 */
export function legacyCreatePdfExportStore() {
  return createStoreInstance(initialState, pdfExportActionsFactory, { storeName: STORE_NAME });
}

/**
 * Registers the PdfExport store with the persistence system.
 * Uses the legacy store name 'sqPdfExportStore' for workstep compatibility.
 * Persisted at WORKSHEET level (formerly persistenceLevel: 'NONE', now changed to persist preferences).
 */
export const usePersistPdfExportStore = () => {
  const register = useRegisterPersistedStore();
  const store = usePdfExportStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqPdfExportStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydratePdfExportState,
    });
  }, [register, store]);
};
